/* eslint-disable no-nested-ternary */
import { useEffect, useState, useRef, cloneElement, ElementType } from "react";

import * as React from "react";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import {
  useFloating,
  Placement,
  Strategy,
  Side,
  useInteractions,
  useHover,
  useFocus,
  useRole,
  arrow,
  useDismiss,
  offset as domOffset,
  autoUpdate,
  shift,
  hide,
  Middleware,
  useMergeRefs,
  useTransitionStyles,
  FloatingPortal,
  FloatingArrow,
  safePolygon,
  limitShift,
} from "@floating-ui/react";
import { warn } from "@utdanningsdirektoratet/utils/assert";
import styles from "./Tooltip.module.less";

type Align = "left" | "right" | "center";
type Position = Side;
type Theme = "dark" | "light";

interface TooltipProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "children"> {
  as?: ElementType;
  disabled?: boolean;
  alwaysShow?: boolean;
  interactive?: boolean;
  customContent?: boolean;
  nowrap?: boolean;
  message?: React.ReactNode | string;
  offset?: { position?: number; align?: number };
  position?: Position;
  align?: Align;
  className?: string;
  tooltipClassName?: string;
  theme?: Theme;
  strategy?: Strategy;
  alert?: boolean;
  children?:
    | ((props: {
        className?: string;
        "aria-describedby"?: string;
        "data-tooltip"?: boolean;
        onBlur?: () => void;
        onFocus?: () => void;
        onKeyDown?: () => void;
        onMouseLeave?: () => void;
        onMouseMove?: () => void;
        onPointerDown?: () => void;
        onPointerEnter?: () => void;
        ref?: any;
      }) => React.ReactNode)
    | React.ReactNode;
}

const getPosition = (position: Position, align?: Align): Placement => {
  if (align === "left") return `${position}-start`;
  if (align === "right") return `${position}-end`;
  return position;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getChildrenIsInline = (type: any) => {
  switch (typeof type) {
    case "symbol":
      return type.toString() === "Symbol(react.fragment)";
    case "object":
      return (
        type.$$typeof &&
        type.$$typeof.toString() !== "Symbol(react.forward_ref)"
      );
    case "string":
      return false;
    case "function":
      return true;
    default:
      warn(
        `Tooltip: Type of children was typeof ${typeof type} which has no switch case. Defaulting to not inline...`,
      );
      return false;
  }
};

const Tooltip = React.forwardRef<HTMLElement, TooltipProps>((props, ref) => {
  const {
    as,
    disabled,
    alwaysShow = false,
    interactive,
    customContent,
    position = "top",
    align = "center",
    children,
    message,
    offset = { position: 8, align: -5 },
    className,
    tooltipClassName,
    theme = "dark",
    nowrap,
    alert = true,
    ...rest
  } = props;

  const isEnabled = !disabled && !!message;

  const arrowRef = useRef(null);
  const [open, setOpen] = useState(alwaysShow && isEnabled);

  const isInline = React.useMemo<boolean>(
    () =>
      as ||
      !React.isValidElement(children) ||
      getChildrenIsInline(children.type),
    [as, children],
  );

  const { refs, context, placement, floatingStyles } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: getPosition(position, align),
    strategy: props.strategy,
    whileElementsMounted: (_reference, _floating, _update) =>
      autoUpdate(_reference, _floating, _update, {
        animationFrame: true,
      }),
    middleware: [
      domOffset({
        mainAxis: offset.position ?? 8,
        alignmentAxis: offset.align ?? -5,
      }),
      shift({ padding: 4, limiter: limitShift() }),
      arrow({ element: arrowRef }),
      hide({ padding: 4 }),
    ] as Array<Middleware>,
  });

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    initial: ({ side }) => ({
      opacity: 0,
      transform: `translate(
        ${side === "left" || side === "right" ? 1.5 * (side === "right" ? -1 : 1) : 0}rem,
        ${side === "top" || side === "bottom" ? 1.5 * (side === "bottom" ? -1 : 1) : 0}rem)`,
    }),
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, {
      enabled: !alwaysShow && isEnabled,
      move: false,
      handleClose: safePolygon(),
    }),
    useFocus(context, { enabled: !alwaysShow && isEnabled }),
    useRole(context, { role: "tooltip" }),
    useDismiss(context, { enabled: !alwaysShow }),
  ]);

  useEffect(() => {
    setOpen(alwaysShow && isEnabled);
  }, [alwaysShow, isEnabled]);

  const tooltipClass = cls(styles.tooltip, tooltipClassName);

  const tooltipWrapperClass = cls({
    [styles.tooltipWrapper]: true,
    [styles.tooltipInteractive]: interactive,
    [styles.tooltipCustomContent]: customContent,
    [styles.tooltipNowrap]: nowrap,
    [styles.tooltipLight]: theme === "light",
  });

  const mergedRef = useMergeRefs([
    ref,
    refs.setReference,
    (children as any)?.ref,
  ]);

  const Tag = as || "span";

  let content;

  if (typeof children === "function") {
    content = children({
      ...getReferenceProps({
        ...rest,
        ref: mergedRef,
        className,
      }),
      "data-tooltip": true,
    });
  } else if (!isInline) {
    content = cloneElement(
      children as React.ReactElement,
      getReferenceProps({
        ...((children as React.ReactElement)?.props ?? {}),
        ...rest,
        ref: mergedRef,
        className: cls(
          (children as React.ReactElement)?.props?.className,
          className,
        ),
        "data-tooltip": true,
      }),
    );
  } else {
    content = (
      <Tag
        {...getReferenceProps({
          ...rest,
          className,
          ref: mergedRef,
        })}
      >
        {children}
      </Tag>
    );
  }

  return (
    <>
      {content}
      {isMounted && (
        <FloatingPortal id="__tooltips">
          <div
            className={tooltipClass}
            style={floatingStyles}
            ref={refs.setFloating}
          >
            <div className={tooltipWrapperClass} style={transitionStyles}>
              <div
                {...getFloatingProps({
                  className: styles.tooltipContent,
                })}
              >
                {message}
              </div>
              <FloatingArrow
                className={styles.tooltipArrow}
                ref={arrowRef}
                data-placement={placement}
                height={8}
                width={16}
                fill={
                  theme === "light" ? "var(--secondary)" : "var(--darkgrey95)"
                }
                context={context}
                staticOffset={
                  (position === "top" || position === "bottom") &&
                  align !== "center"
                    ? "5px"
                    : undefined
                }
              />
            </div>
          </div>
        </FloatingPortal>
      )}
      {alert && (
        <FloatingPortal id="__alerts" preserveTabOrder={false}>
          <div
            role="alert"
            className={cls({
              [styles.tooltipVisuallyHidden]: true,
              [styles.tooltipHidden]: !open,
            })}
          >
            {open ? message : null}
          </div>
        </FloatingPortal>
      )}
    </>
  );
});

export default Tooltip;
